import { BLAST, TRIGGERED } from 'constants/templateTypes';
import { BorderedRadioListField } from 'lib/iterable-hook-form/FormField';
import { CampaignType as CT } from 'modules/domains/campaign';
import styled from 'styled-components';
import { EligibilityCampaign, SendMultiChannel, TriggerWorkflow } from 'ui/icons';
import { BorderedRadioList } from 'ui/radioList';
import { Option } from 'ui/radioList/types';
import { Label, spacing } from 'ui/theme';

const BaseCampaignOption = `
gap: ${spacing.base};

> * {
  display: flex;
  flex: 1;
  margin: 0;
}

> * > * {
  margin-bottom: 0;
}
`;

const FlexOptionStyles = `
${BaseCampaignOption}
display: flex;
flex-direction: row;
align-items: stretch;`;

const CampaignTypeOptions = styled(BorderedRadioListField)`
  ${FlexOptionStyles}
`;

const InvalidOptions = styled(BorderedRadioList)`
  ${FlexOptionStyles}
`;

const EligibilityType = styled(BorderedRadioListField)`
  ${BaseCampaignOption}
  display: grid;
  grid-template-columns: repeat(2, 1fr);
`;

const EmbeddedWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.base};
`;

const Wrapper = styled.div`
  margin-bottom: ${spacing.half};
  margin-top: ${spacing.double};
`;

const API_TRIGGERED_CAMPAIGN_TYPE = {
  description:
    'Sent after Iterable receives a request from your API (for example, an order confirmation)',
  Icon: TriggerWorkflow,
  id: 'triggered-option',
  name: 'API-Triggered',
  value: TRIGGERED,
};

const BLAST_CAMPAIGN_TYPE = {
  description: 'Sent to all of the users on a list (for example, a weekly newsletter)',
  Icon: SendMultiChannel,
  id: 'blast-option',
  name: 'Blast',
  value: BLAST,
};

const BASE_CAMPAIGN_TYPES = [BLAST_CAMPAIGN_TYPE, API_TRIGGERED_CAMPAIGN_TYPE];

const ELIGIBILITY_CAMPAIGN_TYPE = {
  description:
    'Users see this message when viewing your app or website, if they match predefined segmentation criteria.',
  Icon: EligibilityCampaign,
  id: 'eligibility-option',
  name: 'Eligibility',
  value: CT.Eligibility,
};

const UNSUPPORTED_CAMPAIGN_TYPES_FOR_EMBEDDED_TOOLTIP: Record<string, string> = {
  [BLAST]: 'Blast is not available for embedded campaigns',
  [TRIGGERED]: 'API-Triggered is not available for embedded campaigns',
};

const addTooltipToOption = (option: Option, tooltipContent: string) => ({
  ...option,
  isTooltipEnabled: true,
  tooltipContent,
});

const NotBlastEligible = {
  isDisabled: true,
  isTooltipEnabled: true,
  tooltipContent: 'Submit a business-initiated template for approval to send a blast campaign.',
};

const getWhatsAppCampaignTypes = (hasApprovedBusinessTemplates: boolean) => [
  {
    ...BLAST_CAMPAIGN_TYPE,
    description:
      'Sent to all of the users on a list with approved templates (for example, a weekly newsletter)',
    ...(!hasApprovedBusinessTemplates && NotBlastEligible),
  },
  API_TRIGGERED_CAMPAIGN_TYPE,
];

export const BASE_CAMPAIGN_TYPES_ID = 'base-campaign-types';
export const ELIGIBILITY_CAMPAIGN_TYPE_ID = 'eligibility-campaign-type';
export const INVALID_FLEX_CAMPAIGN_TYPES = 'invalid-campaign-types-for-flex';

const EmbeddedCampaignType = () => (
  <EmbeddedWrapper>
    <InvalidOptions
      data-test={INVALID_FLEX_CAMPAIGN_TYPES}
      isReadOnly
      name="invalidTypes"
      options={BASE_CAMPAIGN_TYPES.map((type) =>
        addTooltipToOption(type, UNSUPPORTED_CAMPAIGN_TYPES_FOR_EMBEDDED_TOOLTIP[type.value]),
      )}
      value={'none'}
    />
    <EligibilityType
      data-test={ELIGIBILITY_CAMPAIGN_TYPE_ID}
      name="campaignType"
      options={[ELIGIBILITY_CAMPAIGN_TYPE]}
    />
  </EmbeddedWrapper>
);

type CampaignTypeProps = {
  isEmbeddedMessage: boolean;
  isWhatsAppCampaign: boolean;
  hasApprovedBusinessTemplates: boolean;
};

export const CampaignType = ({
  isEmbeddedMessage,
  isWhatsAppCampaign,
  hasApprovedBusinessTemplates,
}: CampaignTypeProps) => (
  <>
    <Wrapper>
      <Label>Type</Label>
    </Wrapper>

    {isEmbeddedMessage ? (
      <EmbeddedCampaignType />
    ) : (
      <CampaignTypeOptions
        data-test={BASE_CAMPAIGN_TYPES_ID}
        name="campaignType" // RHF field
        options={
          isWhatsAppCampaign
            ? getWhatsAppCampaignTypes(hasApprovedBusinessTemplates)
            : BASE_CAMPAIGN_TYPES
        }
      />
    )}
  </>
);
